import { DayObject, Timestamp } from "@/types/date"
import type { LoyaltyProgramCatalog } from "@/types/loyalty-program"

import { PagingMeta } from "@/lib/paging"
import { baseApi } from "./base-api"

export interface AdminLoyaltyProgramSummary {
  id: number
  program_name: string
  program_type: string
  vendor_name: string | null
  last_crawled_at: DayObject | null
  deactivated_at: Timestamp | null
}

export interface AdminLoyaltyProgramRow extends AdminLoyaltyProgramSummary {
  /** Enabled source pages */
  source_count: number
  /** Enabled source pages whose last fetch failed */
  failing_source_count: number
  last_fetched_at: Timestamp | null
  /** Served rules: the latest reviewed version of each */
  rule_count: number
}

/** The numbers behind the loyalty admin page's explainer and health tabs (GET /api/store-admin/loyalty_overview). */
export interface LoyaltyOverview {
  generated_at: Timestamp
  pipeline: {
    programs: number
    sources: number
    captures: number
    chunks: number
    rules: number
    evidence: number
    open_gaps: number
  }
  programs_by_type: Record<string, number>
  /** Enabled pages by last fetch: day, week, month, older, never */
  source_freshness: Record<string, number>
  fetch_failures: Record<string, number>
  captures_by_day: { day: DayObject; count: number }[]
  rules_by_category: Record<string, number>
  rules_by_program: Record<string, number>
  data_issues_by_category: Record<string, number>
  fast_path: {
    start: string
    /** Each state and the states it may go to */
    states: Record<string, string[]>
    terminals: string[]
  }
  tools: { name: string; description: string }[]
  award_space: {
    rows: number
    fresh_rows: number
    availabilities: number
    by_program: Record<string, number>
    /** How old seats.aero's observation was when fetched: under_6h, under_1d, under_3d, older, unknown */
    seen_age: Record<string, number>
    calls_today: number
  }
}

export interface LoyaltyProgramDetail extends AdminLoyaltyProgramSummary {
  vendor_aliases: string[] | null
  metadata: unknown
  content_fingerprint: string | null
  s3_archive_path: string | null
  deactivation_reason: string | null
  created_at: Timestamp
  updated_at: Timestamp
}

export interface AdminLoyaltyRule {
  id: number
  rule_key: string
  version: number
  category: string
  name: string
  statement: string
  typed: Record<string, unknown>
  conditions: unknown[]
  exceptions: unknown[]
  review_status: string
  effective_from: DayObject | null
  effective_to: DayObject | null
  evidence: {
    id: number
    field: string
    quote: string
    url: string | null
    section: string | null
    captured_at: Timestamp
  }[]
}

export interface LoyaltyProgramSource {
  id: number
  loyalty_program_id: number
  url: string
  discovery_source: string
  source_type: string | null
  enabled: boolean
  s3_archive_path: string | null
  last_fetched_at: Timestamp | null
  last_fetch_failed_at: Timestamp | null
  fetch_failure: string | null
  fetch_failure_count: number
  last_extracted_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
}

export interface LoyaltyProgramSourceReviewRow extends LoyaltyProgramSource {
  program_name: string
  program_type: string
  state: "manual_upload" | "fetch_failed"
  parent_source_id: number | null
  refresh_cadence: "daily" | "weekly" | null
  content_at: Timestamp | null
  programs_with_url: number
}

export interface LoyaltyProgramArtifact {
  path: string
  size: number
  updated_at: Timestamp
  url: string
}

export interface LoyaltyProgramInput {
  program_name: string
  program_type: string
  vendor_name: string | null
  vendor_aliases: string[]
  active: boolean
  deactivation_reason: string | null
}

const baseUrl = "/api/store-admin/loyalty_programs"

export const loyaltyProgramsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLoyaltyProgramCatalog: builder.query<LoyaltyProgramCatalog, void>({
      query: () => ({ url: "/api/loyalty_programs" }),
    }),
    createLoyaltyProgram: builder.mutation<
      AdminLoyaltyProgramSummary,
      LoyaltyProgramInput
    >({
      query: (data) => ({ url: baseUrl, method: "POST", data }),
      invalidatesTags: ["LoyaltyPrograms"],
    }),
    updateLoyaltyProgram: builder.mutation<
      AdminLoyaltyProgramSummary,
      { id: number; data: LoyaltyProgramInput }
    >({
      query: ({ id, data }) => ({
        url: `${baseUrl}/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["LoyaltyPrograms"],
    }),
    createLoyaltyProgramSource: builder.mutation<
      LoyaltyProgramSource,
      { programId: number; url: string; source_type: string }
    >({
      query: ({ programId, ...data }) => ({
        url: `${baseUrl}/${programId}/sources`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { programId }) => [
        { type: "LoyaltySources", id: programId },
      ],
    }),
    getLoyaltyProgramSourceReview: builder.query<
      { data: LoyaltyProgramSourceReviewRow[] },
      void
    >({
      query: () => ({ url: "/api/store-admin/loyalty_program_sources/review" }),
    }),
    createLoyaltyProgramArtifact: builder.mutation<
      { path: string; source_ids: number[] },
      { programId: number; sourceId: number; file: File }
    >({
      query: ({ programId, sourceId, file }) => {
        const data = new FormData()
        data.append("file", file)
        return {
          url: `${baseUrl}/${programId}/sources/${sourceId}/artifacts`,
          method: "POST",
          data,
          headers: { "content-type": "multipart/form-data" },
        }
      },
      invalidatesTags: (_result, _error, { programId, sourceId }) => [
        { type: "LoyaltyArtifacts", id: `${programId}:all` },
        { type: "LoyaltyArtifacts", id: `${programId}:${sourceId}` },
        { type: "LoyaltySources", id: programId },
        { type: "LoyaltySources", id: `${programId}:${sourceId}` },
      ],
    }),
    getLoyaltyProgramArtifacts: builder.query<
      { data: LoyaltyProgramArtifact[] },
      { programId: number; sourceId: number | null }
    >({
      query: ({ programId, sourceId }) => ({
        url:
          sourceId === null
            ? `${baseUrl}/${programId}/artifacts`
            : `${baseUrl}/${programId}/sources/${sourceId}/artifacts`,
      }),
      providesTags: (_result, _error, { programId, sourceId }) => [
        { type: "LoyaltyArtifacts", id: `${programId}:${sourceId ?? "all"}` },
      ],
    }),
    getLoyaltyOverview: builder.query<LoyaltyOverview, void>({
      query: () => ({ url: "/api/store-admin/loyalty_overview" }),
    }),
    getLoyaltyPrograms: builder.query<{ data: AdminLoyaltyProgramRow[] }, void>(
      {
        query: () => ({ url: baseUrl }),
        providesTags: ["LoyaltyPrograms"],
      }
    ),
    getLoyaltyProgram: builder.query<LoyaltyProgramDetail, number>({
      query: (id) => ({ url: `${baseUrl}/${id}` }),
      providesTags: ["LoyaltyPrograms"],
    }),
    getAdminLoyaltyProgramRules: builder.query<
      { data: AdminLoyaltyRule[]; meta: PagingMeta },
      { programId: number; page: number; query: string }
    >({
      query: ({ programId, page, query }) => ({
        url: `${baseUrl}/${programId}/rules`,
        params: { page, query },
      }),
    }),
    getLoyaltyProgramSources: builder.query<
      { data: LoyaltyProgramSource[]; meta: PagingMeta },
      { programId: number; page: number }
    >({
      query: ({ programId, page }) => ({
        url: `${baseUrl}/${programId}/sources`,
        params: { page },
      }),
      providesTags: (_result, _error, { programId }) => [
        { type: "LoyaltySources", id: programId },
      ],
    }),
    getLoyaltyProgramSource: builder.query<
      LoyaltyProgramSource,
      { programId: number; sourceId: number }
    >({
      query: ({ programId, sourceId }) => ({
        url: `${baseUrl}/${programId}/sources/${sourceId}`,
      }),
      providesTags: (_result, _error, { programId, sourceId }) => [
        { type: "LoyaltySources", id: `${programId}:${sourceId}` },
      ],
    }),
  }),
})

export const {
  useGetLoyaltyProgramCatalogQuery,
  useCreateLoyaltyProgramMutation,
  useUpdateLoyaltyProgramMutation,
  useCreateLoyaltyProgramSourceMutation,
  useCreateLoyaltyProgramArtifactMutation,
  useGetLoyaltyProgramArtifactsQuery,
  useGetLoyaltyOverviewQuery,
  useGetLoyaltyProgramsQuery,
  useGetLoyaltyProgramQuery,
  useGetLoyaltyProgramSourcesQuery,
  useGetAdminLoyaltyProgramRulesQuery,
  useGetLoyaltyProgramSourceQuery,
  useLazyGetLoyaltyProgramSourceReviewQuery,
} = loyaltyProgramsApi
